import React, { useEffect, useRef, useState } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { FaSave, FaUpload, FaRobot, FaUndo } from "react-icons/fa";

function InlineSpinner({ size = 14, color = "#fff" }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: `2px solid ${color}`,
        borderTopColor: "transparent",
        borderRadius: "50%",
        animation: "ai-agent-spin 0.7s linear infinite",
        verticalAlign: "middle",
      }}
    />
  );
}

const SKELETON_BG = "#e5e7eb";
const SKELETON_BG_LIGHT = "#f3f4f6";

function SkeletonBlock({ width = "100%", height = 14, radius = 6, marginBottom = 0, light = false, style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: light ? SKELETON_BG_LIGHT : SKELETON_BG,
        marginBottom,
        ...style,
      }}
    />
  );
}

function SkeletonField({ labelWidth = 90, inputHeight = 36, helperWidth = "60%", inputRadius = 8 }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <SkeletonBlock width={labelWidth} height={12} marginBottom={8} />
      <SkeletonBlock width="100%" height={inputHeight} radius={inputRadius} light marginBottom={6} />
      <SkeletonBlock width={helperWidth} height={10} light />
    </div>
  );
}

function AIAgentSettingsSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading AI agent settings"
      aria-busy="true"
      style={{ marginBottom: 8, animation: "ai-agent-skeleton-pulse 1.5s ease-in-out infinite" }}
    >
      <style>{`@keyframes ai-agent-skeleton-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }`}</style>

      {/* Header row (icon + title) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <SkeletonBlock width={16} height={16} radius={4} />
        <SkeletonBlock width={140} height={14} />
      </div>
      <SkeletonBlock width="85%" height={10} light marginBottom={18} />

      {/* Profile photo */}
      <div style={{ marginBottom: 16 }}>
        <SkeletonBlock width={96} height={12} marginBottom={8} />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: SKELETON_BG, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <SkeletonBlock width={130} height={32} radius={8} marginBottom={6} />
            <SkeletonBlock width={200} height={10} light />
          </div>
        </div>
      </div>

      <SkeletonField labelWidth={80} />
      <SkeletonField labelWidth={70} />
      <SkeletonField labelWidth={50} />
      <SkeletonField labelWidth={100} />

      {/* Email signature (taller) */}
      <div style={{ marginBottom: 14 }}>
        <SkeletonBlock width={110} height={12} marginBottom={8} />
        <SkeletonBlock width="100%" height={140} radius={8} light marginBottom={12} />
        <SkeletonBlock width={70} height={10} marginBottom={8} />
        <div style={{ border: `1px solid ${SKELETON_BG}`, borderRadius: 10, padding: "18px 20px" }}>
          <SkeletonBlock width="60%" height={12} light marginBottom={18} />
          <div style={{ display: "flex", gap: 14 }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: SKELETON_BG, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <SkeletonBlock width="40%" height={14} marginBottom={8} />
              <SkeletonBlock width="55%" height={10} light marginBottom={6} />
              <SkeletonBlock width="35%" height={10} light />
            </div>
          </div>
        </div>
      </div>

      {/* Outbound mode card */}
      <div style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${SKELETON_BG}`, background: SKELETON_BG_LIGHT, marginBottom: 10 }}>
        <SkeletonBlock width="55%" height={13} marginBottom={6} />
        <SkeletonBlock width="90%" height={10} light marginBottom={12} />
        <div style={{ display: "inline-flex", gap: 4, padding: 3, borderRadius: 8, background: SKELETON_BG }}>
          <SkeletonBlock width={56} height={22} radius={6} light />
          <SkeletonBlock width={56} height={22} radius={6} light />
          <SkeletonBlock width={56} height={22} radius={6} light />
        </div>
      </div>

      {/* Auto-reply toggle card */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 14px", borderRadius: 10, border: `1px solid ${SKELETON_BG}`,
        background: SKELETON_BG_LIGHT, marginBottom: 14,
      }}>
        <div style={{ flex: 1, marginRight: 12 }}>
          <SkeletonBlock width="40%" height={13} marginBottom={6} />
          <SkeletonBlock width="80%" height={10} light />
        </div>
        <SkeletonBlock width={44} height={24} radius={12} />
      </div>

      <SkeletonField labelWidth={120} inputHeight={110} />

      {/* Save button row */}
      <div style={{ marginTop: 14 }}>
        <SkeletonBlock width={150} height={36} radius={8} />
      </div>
    </div>
  );
}

const DEFAULT_SIGNATURE = [
  "Kind regards,",
  "",
  "{{ai_agent_name}}",
  "{{ai_title}}",
  "{{agency_name}}",
].join("\n");

function deriveStateFromDetails(details) {
  if (!details || typeof details !== "object") return null;
  let rawSettings = details.agent_settings;
  if (typeof rawSettings === "string") {
    try { rawSettings = JSON.parse(rawSettings); } catch { rawSettings = {}; }
  }
  const aiCfg = (rawSettings && typeof rawSettings === "object" ? rawSettings : {}).ai_agent_config || {};
  const aiName = details.ai_agent_name || "";
  const aiFirst = details.ai_agent_first_name || "";
  const fullAiEmail = details.ai_agent_email || "";
  const customDomain = (details.custom_domain || "").trim();
  const domain = customDomain || "homeprice.site";
  let prefix = "";
  if (fullAiEmail && fullAiEmail.includes("@")) {
    prefix = fullAiEmail.split("@")[0];
  }
  if (!customDomain && details.agent_id) {
    const idSuffix = `.${details.agent_id}`;
    if (prefix.toLowerCase().endsWith(idSuffix.toLowerCase())) {
      prefix = prefix.slice(0, prefix.length - idSuffix.length);
    }
  }
  const auto = aiCfg.auto_reply === true;
  const rawMode = (aiCfg.outbound_mode || "").toString().toLowerCase();
  const outboundMode = (rawMode === "on" || rawMode === "off" || rawMode === "review")
    ? rawMode
    : (aiCfg.outbound_campaigns_enabled === true ? "on" : "off");
  const globalInstr = typeof aiCfg.global_instructions === "string" ? aiCfg.global_instructions : "";
  const defaultTitle = `Associate to ${details.agent_name || ""}`.trim();
  const titleVal = typeof aiCfg.ai_title === "string" && aiCfg.ai_title.trim() !== ""
    ? aiCfg.ai_title
    : defaultTitle;
  const signature = typeof aiCfg.email_signature === "string" && aiCfg.email_signature.trim() !== ""
    ? aiCfg.email_signature
    : DEFAULT_SIGNATURE;
  return {
    name: aiName,
    firstName: aiFirst,
    emailPrefix: prefix,
    domain,
    photo: typeof aiCfg.profile_photo === "string" ? aiCfg.profile_photo : "",
    title: titleVal,
    autoReply: auto,
    outboundMode,
    globalInstructions: globalInstr,
    emailSignature: signature,
    signatureLogo: typeof aiCfg.email_signature_logo === "string" ? aiCfg.email_signature_logo : "",
  };
}

export default function AIAgentSettingsPanel({ agentDetails: agentDetailsProp, onSaved }) {
  const [agentDetails, setAgentDetails] = useState(agentDetailsProp || null);
  const [loading, setLoading] = useState(!agentDetailsProp);
  const [aiAgentName, setAiAgentName] = useState("");
  const [aiAgentFirstName, setAiAgentFirstName] = useState("");
  const [aiAgentEmailPrefix, setAiAgentEmailPrefix] = useState("");
  const [aiAgentDomain, setAiAgentDomain] = useState("homeprice.site");
  const [aiAgentPhoto, setAiAgentPhoto] = useState("");
  const [aiTitle, setAiTitle] = useState("");
  const [aiAutoReply, setAiAutoReply] = useState(false);
  const [aiOutboundMode, setAiOutboundMode] = useState("off");
  const [aiGlobalInstructions, setAiGlobalInstructions] = useState("");
  const [aiEmailSignature, setAiEmailSignature] = useState(DEFAULT_SIGNATURE);
  const [aiSignatureLogo, setAiSignatureLogo] = useState("");
  const [originalAiAgent, setOriginalAiAgent] = useState({
    name: "", first_name: "", email_prefix: "", title: "",
    auto_reply: false, outbound_mode: "off",
    global_instructions: "", email_signature: DEFAULT_SIGNATURE,
    signature_logo: "",
  });
  const [savingAiAgent, setSavingAiAgent] = useState(false);
  const [aiAgentSaveMsg, setAiAgentSaveMsg] = useState("");
  const [uploadingAiPhoto, setUploadingAiPhoto] = useState(false);
  const aiPhotoInputRef = useRef(null);
  const [uploadingSignatureLogo, setUploadingSignatureLogo] = useState(false);
  const signatureLogoInputRef = useRef(null);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailEdited, setTestEmailEdited] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailMsg, setTestEmailMsg] = useState("");
  // Server-provided default system prompt. Lazily fetched once so the
  // "Restore Default" button can reset the textarea to the canonical
  // template (placeholders intact — they're substituted at LLM time).
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState("");
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);

  const applyDetails = (details) => {
    setAgentDetails(details);
    const derived = deriveStateFromDetails(details);
    if (!derived) return;
    setAiAgentName(derived.name);
    setAiAgentFirstName(derived.firstName);
    setAiAgentEmailPrefix(derived.emailPrefix);
    setAiAgentDomain(derived.domain);
    setAiAgentPhoto(derived.photo);
    setAiTitle(derived.title);
    setAiAutoReply(derived.autoReply);
    setAiOutboundMode(derived.outboundMode);
    setAiGlobalInstructions(derived.globalInstructions);
    setAiEmailSignature(derived.emailSignature);
    setAiSignatureLogo(derived.signatureLogo);
    setTestEmailTo((prev) => (testEmailEdited && prev ? prev : (details?.agent_email || "")));
    setOriginalAiAgent({
      name: derived.name,
      first_name: derived.firstName,
      email_prefix: derived.emailPrefix,
      title: derived.title,
      auto_reply: derived.autoReply,
      outbound_mode: derived.outboundMode,
      global_instructions: derived.globalInstructions,
      email_signature: derived.emailSignature,
      signature_logo: derived.signatureLogo,
    });
  };

  useEffect(() => {
    if (agentDetailsProp) {
      applyDetails(agentDetailsProp);
      setLoading(false);
      return;
    }
    setLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/authenticate-token-agent`)
      .then((res) => {
        const details = res?.data?.agent_details || {};
        applyDetails(details);
      })
      .catch(() => setAiAgentSaveMsg("Failed to load AI agent settings."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentDetailsProp]);

  // Lazily fetch the canonical default system prompt so "Restore Default"
  // has something to drop into the textarea. Stored on the server so we
  // can tweak it centrally without shipping a new frontend build.
  useEffect(() => {
    let cancelled = false;
    setLoadingDefaultPrompt(true);
    handleTokenAndPostRequest(`${window.api_host}/ai-agent/default-system-prompt`)
      .then((res) => {
        if (cancelled) return;
        const prompt = res?.data?.default_system_prompt;
        if (typeof prompt === "string" && prompt.trim()) {
          setDefaultSystemPrompt(prompt);
        }
      })
      .catch(() => { /* silent — Restore Default will just be disabled */ })
      .finally(() => { if (!cancelled) setLoadingDefaultPrompt(false); });
    return () => { cancelled = true; };
  }, []);

  const handleRestoreDefaultSystemPrompt = () => {
    if (!defaultSystemPrompt) return;
    if (aiGlobalInstructions && aiGlobalInstructions.trim()) {
      const confirmed = window.confirm(
        "Replace the current system prompt with the default? Your existing edits will be lost."
      );
      if (!confirmed) return;
    }
    setAiGlobalInstructions(defaultSystemPrompt);
    setAiAgentSaveMsg("");
  };

  const aiAgentHasChanges = (
    aiAgentName !== originalAiAgent.name ||
    aiAgentFirstName !== originalAiAgent.first_name ||
    aiAgentEmailPrefix !== originalAiAgent.email_prefix ||
    aiTitle !== originalAiAgent.title ||
    aiAutoReply !== originalAiAgent.auto_reply ||
    aiOutboundMode !== originalAiAgent.outbound_mode ||
    aiGlobalInstructions !== originalAiAgent.global_instructions ||
    aiEmailSignature !== originalAiAgent.email_signature ||
    aiSignatureLogo !== originalAiAgent.signature_logo
  );

  const handleSaveAiAgent = async () => {
    setSavingAiAgent(true);
    setAiAgentSaveMsg("");
    try {
      const trimmedPrefix = (aiAgentEmailPrefix || "").trim().toLowerCase();
      if (trimmedPrefix && !/^[a-z0-9._+-]+$/.test(trimmedPrefix)) {
        setAiAgentSaveMsg("Invalid email prefix. Use letters, numbers, dots, +, - or _.");
        setSavingAiAgent(false);
        return;
      }
      const onSharedDomain = aiAgentDomain === "homeprice.site";
      const agentIdForSuffix = agentDetails?.agent_id;
      const prefixToSave = (onSharedDomain && trimmedPrefix && agentIdForSuffix)
        ? `${trimmedPrefix}.${agentIdForSuffix}`
        : trimmedPrefix;
      const payload = {
        ai_agent_name: aiAgentName.trim(),
        ai_agent_first_name: aiAgentFirstName.trim(),
        ai_agent_email_prefix: prefixToSave,
        agent_settings: {
          ai_agent_config: {
            auto_reply: !!aiAutoReply,
            outbound_mode: aiOutboundMode,
            outbound_campaigns_enabled: aiOutboundMode !== "off",
            global_instructions: aiGlobalInstructions,
            ai_title: aiTitle,
            email_signature: aiEmailSignature,
            email_signature_logo: aiSignatureLogo || "",
            profile_photo: aiAgentPhoto || "",
          },
        },
      };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-details`,
        payload,
        { "Content-Type": "application/json" }
      );
      if (res.data.success) {
        setAiAgentSaveMsg("AI agent settings saved successfully.");
        setOriginalAiAgent({
          name: aiAgentName,
          first_name: aiAgentFirstName,
          email_prefix: trimmedPrefix,
          title: aiTitle,
          auto_reply: !!aiAutoReply,
          outbound_mode: aiOutboundMode,
          global_instructions: aiGlobalInstructions,
          email_signature: aiEmailSignature,
          signature_logo: aiSignatureLogo,
        });
        if (typeof onSaved === "function") onSaved();
      } else {
        setAiAgentSaveMsg(res.data.message || "Failed to save AI agent settings.");
      }
    } catch {
      setAiAgentSaveMsg("Failed to save AI agent settings. Please try again.");
    } finally {
      setSavingAiAgent(false);
    }
  };

  const handleAiPhotoUpload = async (file) => {
    if (!file) return;
    setUploadingAiPhoto(true);
    setAiAgentSaveMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("field", "ai_agent_profile_image");
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data.success && res.data.url) {
        setAiAgentPhoto(res.data.url);
      } else {
        setAiAgentSaveMsg(res.data.message || "Failed to upload photo.");
      }
    } catch {
      setAiAgentSaveMsg("Failed to upload photo. Please try again.");
    } finally {
      setUploadingAiPhoto(false);
    }
  };

  const handleSignatureLogoUpload = async (file) => {
    if (!file) return;
    setUploadingSignatureLogo(true);
    setAiAgentSaveMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("field", "ai_agent_signature_logo");
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data.success && res.data.url) {
        setAiSignatureLogo(res.data.url);
      } else {
        setAiAgentSaveMsg(res.data.message || "Failed to upload signature logo.");
      }
    } catch {
      setAiAgentSaveMsg("Failed to upload signature logo. Please try again.");
    } finally {
      setUploadingSignatureLogo(false);
    }
  };

  const handleSendTestEmail = async () => {
    const to = (testEmailTo || "").trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setTestEmailMsg("Enter a valid email address.");
      return;
    }
    setSendingTestEmail(true);
    setTestEmailMsg("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/ai-agent/send-test-email`,
        { to_email: to },
        { "Content-Type": "application/json" }
      );
      if (res?.data?.status === "success") {
        setTestEmailMsg(`Sent to ${res.data.sent_to}. Check your inbox.`);
      } else {
        setTestEmailMsg(res?.data?.message || "Failed to send test email.");
      }
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      setTestEmailMsg(apiMsg || "Failed to send test email. Please try again.");
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return <AIAgentSettingsSkeleton />;
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <style>{`@keyframes ai-agent-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <FaRobot size={16} color="#7c3aed" />
        <span style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>
          AI Agent Persona
        </span>
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
        Configure the AI persona that handles inbound conversations. It uses these details when drafting and (optionally) auto-sending replies.
      </p>

      {/* System prompt (formerly Global Instructions) — moved to the top
          because it now owns the AI's full persona / voice / style rules
          for every message it drafts (outbound follow-ups, inbound email
          replies and inbound SMS replies). */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 5, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
            System Prompt
          </label>
          <button
            type="button"
            onClick={handleRestoreDefaultSystemPrompt}
            disabled={!defaultSystemPrompt || loadingDefaultPrompt}
            title={defaultSystemPrompt ? "Replace the current system prompt with the default" : "Default prompt unavailable"}
            style={{
              padding: "5px 10px", borderRadius: 6,
              border: "1px solid #d1d5db", background: "#fff",
              color: !defaultSystemPrompt || loadingDefaultPrompt ? "#9ca3af" : "#374151",
              fontSize: 12, fontWeight: 600,
              cursor: !defaultSystemPrompt || loadingDefaultPrompt ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <FaUndo size={10} /> Restore Default
          </button>
        </div>
        <textarea
          value={aiGlobalInstructions}
          onChange={(e) => { setAiGlobalInstructions(e.target.value); setAiAgentSaveMsg(""); }}
          placeholder="The full instructions your AI follows when drafting messages."
          rows={20}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 13,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            boxSizing: "border-box", lineHeight: 1.5, resize: "vertical", minHeight: 320,
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, lineHeight: 1.5 }}>
          The complete system prompt your AI uses when drafting outbound
          follow-ups, inbound email replies and SMS replies. The placeholders{" "}
          <code>{"{ai_agent_name}"}</code>, <code>{"{ai_first_name}"}</code>,{" "}
          <code>{"{human_agent_name}"}</code> and <code>{"{agency_name}"}</code>{" "}
          are auto-filled at runtime. Use "Restore Default" to revert to the standard template.
        </div>
      </div>

      {/* Profile photo */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
          Profile Photo
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: aiAgentPhoto ? `url(${aiAgentPhoto}) center/cover` : "#f3f4f6",
            border: "1px solid #e5e7eb", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {!aiAgentPhoto && <FaRobot size={26} color="#9ca3af" />}
          </div>
          <div>
            <input
              ref={aiPhotoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif"
              onChange={(e) => handleAiPhotoUpload(e.target.files?.[0])}
              style={{ display: "none" }}
            />
            <button
              onClick={() => aiPhotoInputRef.current?.click()}
              disabled={uploadingAiPhoto}
              style={{
                padding: "7px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13,
                cursor: uploadingAiPhoto ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              {uploadingAiPhoto ? <InlineSpinner size={12} color="#374151" /> : <FaUpload size={11} />}
              {aiAgentPhoto ? "Replace photo" : "Upload photo"}
            </button>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>PNG, JPG or GIF. Square images work best.</div>
          </div>
        </div>
      </div>

      {/* Full Name */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
          Full Name
        </label>
        <input
          type="text"
          value={aiAgentName}
          onChange={(e) => { setAiAgentName(e.target.value); setAiAgentSaveMsg(""); }}
          placeholder="e.g. Alex Mitchell"
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
          Displayed as the From name on outbound AI replies.
        </div>
      </div>

      {/* First Name */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
          First Name
        </label>
        <input
          type="text"
          value={aiAgentFirstName}
          onChange={(e) => { setAiAgentFirstName(e.target.value); setAiAgentSaveMsg(""); }}
          placeholder="e.g. Alex"
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
          Used in personalised greetings within AI-generated emails.
        </div>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
          Title
        </label>
        <input
          type="text"
          value={aiTitle}
          onChange={(e) => { setAiTitle(e.target.value); setAiAgentSaveMsg(""); }}
          placeholder={`Associate to ${agentDetails?.agent_name || "you"}`}
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
          Shown beneath the AI agent's name on outbound emails (e.g. signature). Defaults to "Associate to {agentDetails?.agent_name || "your name"}".
        </div>
      </div>

      {/* Email prefix */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
          Email Address
        </label>
        <div style={{ display: "flex", alignItems: "stretch", border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
          <input
            type="text"
            value={aiAgentEmailPrefix}
            onChange={(e) => {
              const v = e.target.value.toLowerCase().replace(/[^a-z0-9._+-]/g, "");
              setAiAgentEmailPrefix(v);
              setAiAgentSaveMsg("");
            }}
            placeholder="alex"
            style={{
              flex: 1, minWidth: 0, padding: "8px 12px", border: "none",
              fontSize: 14, outline: "none",
            }}
          />
          <span style={{
            display: "flex", alignItems: "center", padding: "0 12px",
            background: "#f9fafb", borderLeft: "1px solid #e5e7eb",
            color: "#6b7280", fontSize: 13, fontFamily: "monospace",
          }}>
            {aiAgentDomain === "homeprice.site" && agentDetails?.agent_id
              ? `.${agentDetails.agent_id}@${aiAgentDomain}`
              : `@${aiAgentDomain}`}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
          {aiAgentDomain === "homeprice.site" ? (
            <>
              No custom domain set — your AI will send from{" "}
              <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 4 }}>
                {(aiAgentEmailPrefix || "alex").trim().toLowerCase()}
                {agentDetails?.agent_id ? `.${agentDetails.agent_id}` : ""}@homeprice.site
              </code>
              . Contact your account manager to set up a custom domain so your AI can send from your own brand.
            </>
          ) : (
            `Sending from your custom domain @${aiAgentDomain}.`
          )}
        </div>
      </div>

      {/* Email signature */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
          Email Signature
        </label>
        <textarea
          value={aiEmailSignature}
          onChange={(e) => { setAiEmailSignature(e.target.value); setAiAgentSaveMsg(""); }}
          rows={7}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 8,
            border: "1px solid #d1d5db", fontSize: 13.5, fontFamily: "inherit",
            boxSizing: "border-box", lineHeight: 1.5, resize: "vertical",
          }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, lineHeight: 1.5 }}>
          Appended to every email the AI sends to your contacts. Available variables:{" "}
          <code>{"{{ai_agent_name}}"}</code>, <code>{"{{ai_agent_first_name}}"}</code>,{" "}
          <code>{"{{ai_title}}"}</code>, <code>{"{{agency_name}}"}</code>,{" "}
          <code>{"{{ai_agent_email}}"}</code>. Your AI agent's profile photo is shown automatically next to the signature.
        </div>

        {/* Signature logo (optional — falls back to the agency logo) */}
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
            Signature Logo <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 120, height: 56, borderRadius: 8,
              background: aiSignatureLogo ? `url(${aiSignatureLogo}) center/contain no-repeat` : "#f3f4f6",
              border: "1px solid #e5e7eb", flexShrink: 0,
            }} />
            <div>
              <input
                ref={signatureLogoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif"
                onChange={(e) => handleSignatureLogoUpload(e.target.files?.[0])}
                style={{ display: "none" }}
              />
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => signatureLogoInputRef.current?.click()}
                  disabled={uploadingSignatureLogo}
                  style={{
                    padding: "7px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                    background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13,
                    cursor: uploadingSignatureLogo ? "not-allowed" : "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  {uploadingSignatureLogo ? <InlineSpinner size={12} color="#374151" /> : <FaUpload size={11} />}
                  {aiSignatureLogo ? "Replace logo" : "Upload logo"}
                </button>
                {aiSignatureLogo && (
                  <button
                    type="button"
                    onClick={() => { setAiSignatureLogo(""); setAiAgentSaveMsg(""); }}
                    style={{
                      padding: "7px 12px", borderRadius: 8, border: "1px solid #d1d5db",
                      background: "#fff", color: "#b91c1c", fontWeight: 600, fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Shown at the bottom of the signature. If left empty, your agency logo is used.
              </div>
            </div>
          </div>
        </div>

        {(() => {
          const agencyName = agentDetails?.agency_name_long || "";
          const previewPrefix = (aiAgentEmailPrefix || "").trim().toLowerCase() || "ai";
          const previewSuffix = (aiAgentDomain === "homeprice.site" && agentDetails?.agent_id)
            ? `.${agentDetails.agent_id}`
            : "";
          const fullEmail = `${previewPrefix}${previewSuffix}@${aiAgentDomain}`;
          const rawBrand = (agentDetails?.agent_colour || "").trim();
          const brand = (rawBrand && rawBrand.startsWith("#") && (rawBrand.length === 4 || rawBrand.length === 7))
            ? rawBrand : "#0a9fa6";
          const agencyLogo = aiSignatureLogo || agentDetails?.logo_url || "";
          const rendered = (aiEmailSignature || "")
            .replace(/\{\{ai_agent_name\}\}/g, aiAgentName || "")
            .replace(/\{\{ai_agent_first_name\}\}/g, aiAgentFirstName || "")
            .replace(/\{\{ai_title\}\}/g, aiTitle || "")
            .replace(/\{\{agency_name_long\}\}/g, agencyName)
            .replace(/\{\{agency_name\}\}/g, agencyName)
            .replace(/\{\{ai_agent_email\}\}/g, fullEmail);
          const nameNorm = (aiAgentName || "").trim().toLowerCase();
          const initial = (aiAgentName || "A").trim().charAt(0).toUpperCase();
          const lines = rendered.replace(/\r\n/g, "\n").split("\n").map(l => l.replace(/\s+$/, ""));
          let bolded = false;
          const renderedNodes = lines.map((line, i) => {
            const stripped = line.trim();
            if (!stripped) {
              return <div key={i} style={{ height: 8 }}>&nbsp;</div>;
            }
            const isEmail = stripped.indexOf("@") !== -1 && stripped.indexOf(" ") === -1
              && stripped.split("@").pop().indexOf(".") !== -1;
            if (isEmail) {
              return (
                <div key={i} style={{ fontSize: 13, lineHeight: 1.5, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                  <a href={`mailto:${stripped}`} style={{ color: "#374151", textDecoration: "none" }}>{stripped}</a>
                </div>
              );
            }
            if (!bolded && nameNorm && stripped.toLowerCase() === nameNorm) {
              bolded = true;
              return (
                <div key={i} style={{ fontWeight: 700, color: "#111827", fontSize: 15, lineHeight: 1.4 }}>{stripped}</div>
              );
            }
            return (
              <div key={i} style={{ color: "#374151", fontSize: 13, lineHeight: 1.5 }}>{stripped}</div>
            );
          });

          return (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Preview
              </div>
              <div style={{
                border: "1px solid #e5e7eb", borderRadius: 10, background: "#ffffff",
                padding: "18px 20px", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827",
              }}>
                <div style={{ fontSize: 14, lineHeight: 1.6, color: "#9ca3af", fontStyle: "italic", marginBottom: 18 }}>
                  [Your AI's reply to the contact appears here]
                </div>
                <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, display: "flex", alignItems: "flex-start", gap: 14 }}>
                  {aiAgentPhoto ? (
                    <img
                      src={aiAgentPhoto}
                      alt={aiAgentName || ""}
                      style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "1px solid #e5e7eb", flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{
                      width: 64, height: 64, borderRadius: "50%", background: brand,
                      color: "#fff", fontSize: 26, fontWeight: 700, lineHeight: "64px",
                      textAlign: "center", flexShrink: 0,
                    }}>
                      {initial}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renderedNodes}
                    {agencyLogo && (
                      <div style={{ marginTop: 12 }}>
                        <img
                          src={agencyLogo}
                          alt={agencyName}
                          style={{ display: "block", maxHeight: 40, maxWidth: 200, width: "auto", height: "auto" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Send test email */}
      <div style={{
        marginBottom: 14, padding: "12px 14px", borderRadius: 10,
        border: "1px solid #e5e7eb", background: "#f9fafb",
      }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
          Send a sample email
        </label>
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.5 }}>
          Sends a generic introduction from your AI agent to the address below so you can preview
          the layout, signature and sender exactly as your contacts will see it.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
          <input
            type="email"
            value={testEmailTo}
            onChange={(e) => { setTestEmailTo(e.target.value); setTestEmailEdited(true); setTestEmailMsg(""); }}
            placeholder="recipient@example.com"
            style={{
              flex: "1 1 220px", minWidth: 0, padding: "9px 12px",
              borderRadius: 8, border: "1px solid #d1d5db",
              fontSize: 13.5, fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={handleSendTestEmail}
            disabled={sendingTestEmail || !testEmailTo.trim()}
            style={{
              padding: "9px 16px", borderRadius: 8, border: "none",
              background: sendingTestEmail || !testEmailTo.trim() ? "#9ca3af" : "#7c3aed",
              color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: sendingTestEmail || !testEmailTo.trim() ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {sendingTestEmail ? "Sending…" : "Send test email"}
          </button>
        </div>
        {testEmailMsg && (
          <div style={{
            marginTop: 8, fontSize: 12,
            color: testEmailMsg.startsWith("Sent to") ? "#047857" : "#b91c1c",
          }}>
            {testEmailMsg}
          </div>
        )}
      </div>

      {/* Outbound mode */}
      <div style={{
        padding: "12px 14px", borderRadius: 10,
        border: "1px solid #e5e7eb",
        background: aiOutboundMode === "on" ? "#ecfdf5"
                 : aiOutboundMode === "review" ? "#f5f3ff"
                 : "#f9fafb",
        marginBottom: 10,
      }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 2 }}>
            {aiOutboundMode === "on"
              ? "Outbound AI follow-up & warm-up campaigns: AUTO"
              : aiOutboundMode === "review"
              ? "Outbound AI follow-up & warm-up campaigns: REVIEW"
              : "Outbound AI follow-up & warm-up campaigns: OFF"}
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4 }}>
            {aiOutboundMode === "on"
              ? "The AI will email your contacts on your behalf — property update warm-ups, market update warm-ups and incomplete-analysis nudges."
              : aiOutboundMode === "review"
              ? "Drafted outbound emails appear in AI Conversations → Needs Review for you to approve, edit, or dismiss before they send. Nothing goes out without your sign-off."
              : "No outbound follow-up or warm-up emails will be sent to your contacts on your behalf."}
          </div>
        </div>
        <div
          role="radiogroup"
          aria-label="Outbound AI follow-up mode"
          style={{
            display: "inline-flex", padding: 3, borderRadius: 8,
            background: "#e5e7eb", gap: 0,
          }}
        >
          {[
            { key: "off", label: "Off", activeBg: "#9ca3af" },
            { key: "on", label: "Auto", activeBg: "#10b981" },
            { key: "review", label: "Review", activeBg: "#7c3aed" },
          ].map(opt => {
            const active = aiOutboundMode === opt.key;
            return (
              <button
                key={opt.key}
                role="radio"
                aria-checked={active}
                onClick={() => { setAiOutboundMode(opt.key); setAiAgentSaveMsg(""); }}
                style={{
                  padding: "5px 14px", borderRadius: 6, border: "none",
                  background: active ? opt.activeBg : "transparent",
                  color: active ? "#fff" : "#374151",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                  transition: "background 0.15s, color 0.15s",
                  minWidth: 56,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Auto-reply toggle */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 14px", borderRadius: 10,
        border: "1px solid #e5e7eb", background: aiAutoReply ? "#fef3c7" : "#f0fdfa",
        marginBottom: 14,
      }}>
        <div style={{ flex: 1, marginRight: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
            {aiAutoReply ? "Auto-reply enabled" : "Review all responses"}
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4 }}>
            {aiAutoReply
              ? "The AI will send replies directly to contacts without your approval."
              : "All AI-generated replies appear in AI Conversations → Needs Review (and are emailed to you) for review and approval."}
          </div>
        </div>
        <button
          onClick={() => { setAiAutoReply(prev => !prev); setAiAgentSaveMsg(""); }}
          style={{
            width: 44, height: 24, borderRadius: 12, border: "none",
            background: aiAutoReply ? "#f59e0b" : "#10b981",
            cursor: "pointer", position: "relative", transition: "background 0.2s",
            flexShrink: 0,
          }}
        >
          <span style={{
            position: "absolute", top: 2, left: aiAutoReply ? 22 : 2,
            width: 20, height: 20, borderRadius: "50%", background: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s",
          }} />
        </button>
      </div>

      {/* Global Instructions / System Prompt — moved to the top of the panel. */}

      {/* Save button */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
        <button
          onClick={handleSaveAiAgent}
          disabled={!aiAgentHasChanges || savingAiAgent}
          style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: aiAgentHasChanges ? "linear-gradient(90deg, #1a3f6b, #7c3aed)" : "#d1d5db",
            color: "#fff", fontWeight: 600, fontSize: 14,
            cursor: aiAgentHasChanges ? "pointer" : "default",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {savingAiAgent ? <><InlineSpinner size={13} color="#fff" /> Saving...</> : <><FaSave size={11} /> Save AI Agent</>}
        </button>
        {aiAgentSaveMsg && (
          <span style={{
            fontSize: 13, fontWeight: 500,
            color: aiAgentSaveMsg.includes("success") ? "#10b981" : "#e53935",
          }}>
            {aiAgentSaveMsg}
          </span>
        )}
      </div>
    </div>
  );
}
